import { Dispatch, createContext, useEffect, useReducer, Context } from 'react';
import { LanguagesType } from '../../packages/config';
import { Instruments, OrderUnitType, Symbols, SymbolsBySpot, Ticker } from '../../packages/type';
import {
  TypesPosition,
  TypesOrder,
  TypesTpslOrder,
  TypesLeverageInfo,
  TypesTriggerOrder,
  TypesTrailingStopOrders,
  TypesTicker,
  TypesInstruments,
  SignalBots,
  TwapBots,
  TypesMarginMode,
  TypesTradeMode,
  TypesMarginMode_lower,
} from '@blofin/blofin-utils';
export interface Fiat {
  name: string;
  rate: string;
  full_name: string;
  precision: string;
  symbol: string;
}

export interface Rates {
  usdt_usd: string;
  fiat: Fiat[];
}

export interface UserInfo {
  nick_name: string;
  profile: string;
  state: number;
  uid: string;
  user_level: number;
  api_key_permission: boolean;
  vip_level: number;
  account_type: string; //MAIN 主账户，NORMAL_SUB_ACCOUNT 普通子账户，
  switch_able: boolean;
  messages: {
    [key: string]: string;
  };
}

interface WebSocketContextProps {
  ws: any;
  privateWs: any;
  changeCurrency: (symbol: string) => void;
  publicReconect: number;
  privateReconect: number;
  copyTradingPriveReconnect: number;
}

type TypesSyncPosLeverageInfo = (marketId: string, marginMode: TypesMarginMode_lower) => void;

export interface State {
  userInfo: UserInfo | null;
  baseUrl: string | null;
  http?: any | null;
  rates: Rates | null;
  lang?: LanguagesType;
  instruments?: Record<string, Instruments>;
  instrumentsByFutures?: Record<string, TypesInstruments>;
  symbols?: Symbols[];
  symbolsKeyby?: Record<string, Symbols>;
  symbolsBySpot?: SymbolsBySpot[];
  symbolsKeybyBySpot?: Record<string, SymbolsBySpot>;
  ticker?: Record<string, Ticker>;
  tickerByFutures?: Record<string, TypesTicker>;
  orderUnitType?: OrderUnitType;
  userViewType?: string;
  isDemoTrading?: boolean;
  userAccountType?: string;
  messages: {
    [key: string]: any;
  };
  positions?: TypesPosition[];
  orders?: TypesOrder[];
  newOrders?: TypesOrder[];
  tpslOrders?: TypesTpslOrder[];
  newTpslOrders?: TypesTpslOrder[];
  triggerOrders?: TypesTriggerOrder[];
  newTriggerOrders?: TypesTriggerOrder[];
  trailingStopOrders?: TypesTrailingStopOrders[];
  cancelAllOrdersOpen?: boolean;
  changeCurrency?: any;
  leverageInfo?: TypesLeverageInfo;
  signalBots?: SignalBots[];
  twapBots?: TwapBots[];
  closeAllPositionsOpen?: boolean;
  reversePositionsOpen?: boolean;
  actionPrice?: string;
  setActionPrice?: (price: string) => void;
  marginMode?: TypesMarginMode;
  tradeMode?: TypesTradeMode;
  syncPosLeverageInfo?: TypesSyncPosLeverageInfo;
}

type Action =
  | { type: 'ADD_SYMBOLS'; playload: { symbolsKeyby: Record<string, Symbols>; symbols: Symbols[] } }
  | {
      type: 'ADD_SYMBOLS_SPOT';
      playload: { symbolsKeybyBySpot: Record<string, SymbolsBySpot>; symbolsBySpot: SymbolsBySpot[] };
    }
  | { type: 'ADD_USER'; playload: UserInfo }
  | { type: 'ADD_INSTRUMENTS'; playload: Record<string, Instruments> }
  | { type: 'ADD_INSTRUMENTS_BY_FUTURES'; playload: Record<string, TypesInstruments> }
  | { type: 'ADD_TICKER'; playload: Record<string, Ticker> }
  | { type: 'ADD_TICKER_BY_FUTURES'; playload: Record<string, TypesTicker> }
  | { type: 'ADD_ORDER_UNIT_TYPE'; playload: OrderUnitType }
  | { type: 'ADD_RATES'; playload: Rates }
  | { type: 'ADD_USER_VIEW_TYPE'; playload: string }
  | { type: 'ADD_IS_DEMO_TRADING'; playload: boolean }
  | {
      type: 'ADD_MESSAGES';
      playload: {
        [key: string]: string;
      };
    }
  | { type: 'ADD_POSITIONS'; playload: TypesPosition[] }
  | { type: 'ADD_ORDERS'; playload: TypesOrder[] }
  | { type: 'ADD_NEW_ORDERS'; playload: TypesOrder[] }
  | { type: 'ADD_TPSL_ORDERS'; playload: TypesTpslOrder[] }
  | { type: 'ADD_NEW_TPSL_ORDERS'; playload: TypesTpslOrder[] }
  | { type: 'ADD_TRIGGER_ORDERS'; playload: TypesTriggerOrder[] }
  | { type: 'ADD_NEW_TRIGGER_ORDERS'; playload: TypesTriggerOrder[] }
  | { type: 'ADD_CANCEL_ALL_ORDERS_OPEN'; playload: boolean }
  | { type: 'ADD_CHANGE_CURRENCY'; playload: any }
  | { type: 'ADD_LEVERAGE_INFO'; playload: TypesLeverageInfo }
  | { type: 'ADD_TRAILINGSTOP_ORDERS'; playload: TypesTrailingStopOrders[] }
  | { type: 'ADD_SIGNAL_BOTS'; playload: SignalBots[] }
  | { type: 'ADD_TWAP_BOTS'; playload: TwapBots[] }
  | { type: 'ADD_CLOSE_ALL_POSITIONS_OPEN'; playload: boolean }
  | { type: 'ADD_REVERSE_POSITIONS_OPEN'; playload: boolean }
  | { type: 'ADD_ACTION_PRICE'; playload: string }
  | { type: 'ADD_SET_ACTION_PRICE'; playload: (price: string) => void }
  | { type: 'ADD_MARGIN_MODE'; playload: TypesMarginMode }
  | { type: 'ADD_TRADE_MODE'; playload: TypesTradeMode }
  | { type: 'ADD_SYNC_POS_LEVERAGEINFO'; playload: TypesSyncPosLeverageInfo };

export interface StoreContextType {
  state: State;
  dispatch: Dispatch<Action>;
}

const BlofinContext = createContext<StoreContextType>({
  state: {} as State,
  dispatch: () => {},
});

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case 'ADD_SYMBOLS':
      return {
        ...state,
        symbols: action.playload.symbols,
        symbolsKeyby: action.playload.symbolsKeyby,
      };
    case 'ADD_SYMBOLS_SPOT':
      return {
        ...state,
        symbolsBySpot: action.playload.symbolsBySpot,
        symbolsKeybyBySpot: action.playload.symbolsKeybyBySpot,
      };
    case 'ADD_USER':
      return { ...state, userInfo: action.playload };
    case 'ADD_INSTRUMENTS':
      return { ...state, instruments: action.playload };
    case 'ADD_INSTRUMENTS_BY_FUTURES':
      return { ...state, instrumentsByFutures: action.playload };
    case 'ADD_TICKER':
      return { ...state, ticker: action.playload };
    case 'ADD_TICKER_BY_FUTURES':
      return { ...state, tickerByFutures: action.playload };
    case 'ADD_ORDER_UNIT_TYPE':
      return { ...state, orderUnitType: action.playload };
    case 'ADD_RATES':
      return { ...state, rates: action.playload };
    case 'ADD_USER_VIEW_TYPE':
      return { ...state, userViewType: action.playload };
    case 'ADD_IS_DEMO_TRADING':
      return { ...state, isDemoTrading: action.playload };
    case 'ADD_MESSAGES':
      return { ...state, messages: action.playload };
    case 'ADD_POSITIONS':
      return { ...state, positions: action.playload };
    case 'ADD_ORDERS':
      return { ...state, orders: action.playload };
    case 'ADD_NEW_ORDERS':
      return { ...state, newOrders: action.playload };
    case 'ADD_TPSL_ORDERS':
      return { ...state, tpslOrders: action.playload };
    case 'ADD_NEW_TPSL_ORDERS':
      return { ...state, newTpslOrders: action.playload };
    case 'ADD_NEW_TRIGGER_ORDERS':
      return { ...state, newTriggerOrders: action.playload };
    case 'ADD_TRIGGER_ORDERS':
      return { ...state, triggerOrders: action.playload };
    case 'ADD_CANCEL_ALL_ORDERS_OPEN':
      return { ...state, cancelAllOrdersOpen: action.playload };
    case 'ADD_CHANGE_CURRENCY':
      return { ...state, changeCurrency: action.playload };
    case 'ADD_LEVERAGE_INFO':
      return { ...state, leverageInfo: action.playload };
    case 'ADD_TRAILINGSTOP_ORDERS':
      return { ...state, trailingStopOrders: action.playload };
    case 'ADD_SIGNAL_BOTS':
      return { ...state, signalBots: action.playload };
    case 'ADD_TWAP_BOTS':
      return { ...state, twapBots: action.playload };
    case 'ADD_CLOSE_ALL_POSITIONS_OPEN':
      return { ...state, closeAllPositionsOpen: action.playload };
    case 'ADD_REVERSE_POSITIONS_OPEN':
      return { ...state, reversePositionsOpen: action.playload };
    case 'ADD_ACTION_PRICE':
      return { ...state, actionPrice: action.playload };
    case 'ADD_SET_ACTION_PRICE':
      return { ...state, setActionPrice: action.playload };
    case 'ADD_MARGIN_MODE':
      return { ...state, marginMode: action.playload };
    case 'ADD_TRADE_MODE':
      return { ...state, tradeMode: action.playload };
    case 'ADD_SYNC_POS_LEVERAGEINFO':
      return { ...state, syncPosLeverageInfo: action.playload };
    default:
      return state;
  }
};

const BlofinProvider = ({ children, value }: { children: React.ReactNode; value: State }) => {
  const [state, dispatch] = useReducer(reducer, value);

  useEffect(() => {
    if (value.userInfo) {
      dispatch({ type: 'ADD_USER', playload: value.userInfo });
    }
    if (value.rates) {
      dispatch({ type: 'ADD_RATES', playload: value.rates });
    }
    if (value.messages) {
      dispatch({ type: 'ADD_MESSAGES', playload: value.messages });
    }
  }, [value]);

  return <BlofinContext.Provider value={{ state, dispatch }}>{children}</BlofinContext.Provider>;
};

const getUserViewType = (cookieUserViewType?: string) => {
  const userViewType = cookieUserViewType || 'real-trading';
  return {
    userViewType: userViewType,
    isDemoTrading: Boolean(userViewType === 'demo-trading'),
  };
};

export { BlofinProvider, BlofinContext, getUserViewType };
